import {
  AI_AUTOPILOT_USER_ID,
  CHATBOT_USER_ID,
} from "@redotech/ai-autopilot-constants/constant";
import {
  ContentType,
  ConversationMessageType,
  ConversationPlatform,
  ConversationStatus,
  MessageVisibility,
} from "@redotech/redo-model/conversation";
import { ConversationMessage } from "@redotech/redo-model/conversation/conversation-message-db-parser";
import { Conversation } from "@redotech/redo-model/conversation/conversation-schema-db-parser";
import { TeamInfo } from "@redotech/redo-model/team";
import { ConversationType } from "../contexts/active-conversation";
import { getInitialChatFlowStep } from "./chat-flows-utils";

export const TEMP_CONVERSATION_ID = "temp";

export function optimisticallyAddCustomerMessage(
  message: string,
  conversation: Conversation | undefined,
  conversationType: ConversationType,
  teamInfo: TeamInfo,
): Conversation {
  const tempMessage = createTempMessage(
    message,
    conversationType,
    ConversationMessageType.CUSTOMER,
  );

  if (conversation) {
    const newMessages = [...conversation.messages, tempMessage];
    return {
      ...conversation,
      messages: newMessages,
      lastCustomerResponseAt: new Date(),
    };
  } else {
    return createTempConversation([tempMessage], conversationType, teamInfo);
  }
}

export function createTempConversation(
  messages: ConversationMessage[],
  conversationType: ConversationType,
  teamInfo: TeamInfo,
  shouldGetInitialMessages: boolean = true,
): Conversation {
  let initialMessages = messages;
  let lastKnownStepId =
    conversationType === ConversationType.CHAT_FLOW ? 1 : null;

  if (shouldGetInitialMessages) {
    // If messages are empty and we have team info, get initial chat flow step and messages
    if (conversationType === ConversationType.CHAT_FLOW) {
      const { initialChatFlowStep, initialMessages: flowMessages } =
        getInitialChatFlowStep(teamInfo);
      lastKnownStepId = initialChatFlowStep;

      // Add any initial messages from SEND_MESSAGE steps
      if (flowMessages.length > 0) {
        const tempFlowMessages = flowMessages.map((message) =>
          createTempMessage(
            message,
            conversationType,
            ConversationMessageType.MERCHANT,
          ),
        );
        initialMessages = [...tempFlowMessages, ...initialMessages];
      }
    } else if (conversationType === ConversationType.AI) {
      const welcomeMessage = createTempMessage(
        teamInfo.conciergeSettings.general.welcomeMessage,
        conversationType,
        ConversationMessageType.MERCHANT,
      );
      initialMessages = [welcomeMessage, ...initialMessages];
    }
  }

  return {
    _id: TEMP_CONVERSATION_ID,
    assignee:
      conversationType === ConversationType.AI
        ? AI_AUTOPILOT_USER_ID
        : CHATBOT_USER_ID,
    team: teamInfo.id,
    messages: initialMessages,
    createdAt: new Date(),
    updatedAt: new Date(),
    platform: ConversationPlatform.REDO_CHAT,
    status: ConversationStatus.OPEN,
    lastResponseAt: new Date(),
    lastKnownStepId,
  };
}

export function createTempMessage(
  message: string,
  conversationType: ConversationType,
  type: ConversationMessageType,
): ConversationMessage {
  return {
    _id: TEMP_CONVERSATION_ID,
    user:
      conversationType === ConversationType.AI
        ? AI_AUTOPILOT_USER_ID
        : CHATBOT_USER_ID,
    contentType: ContentType.TEXT,
    type,
    content: message,
    sentAt: new Date(),
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

export function isTempConversation(conversation: Conversation): boolean {
  return conversation._id === TEMP_CONVERSATION_ID;
}

export const isUserAssignedConversation = (
  userId: string,
  assignee: string | { _id: string } | undefined | null,
): boolean => {
  if (!assignee) return false;
  const assigneeId = typeof assignee === "string" ? assignee : assignee._id;
  return assigneeId === userId;
};

export const getNewConversationType = (
  isConciergeLiveChat: boolean,
  conversation: Conversation | undefined,
): ConversationType => {
  if (conversation && conversation.platform === ConversationPlatform.EMAIL) {
    return ConversationType.EMAIL;
  }

  const isAssignedToAI =
    isUserAssignedConversation(AI_AUTOPILOT_USER_ID, conversation?.assignee) ||
    isUserAssignedConversation(CHATBOT_USER_ID, conversation?.assignee);
  if (
    (conversation &&
      conversation._id !== TEMP_CONVERSATION_ID &&
      !conversation.assignee) ||
    (conversation?.assignee && !isAssignedToAI)
  ) {
    return ConversationType.LIVE;
  }

  if (!isConciergeLiveChat) {
    return ConversationType.CHAT_FLOW;
  } else {
    return ConversationType.AI;
  }
};

export const getConversationType = (
  platform: ConversationPlatform,
  assignee: string | undefined,
): ConversationType => {
  if (platform === ConversationPlatform.EMAIL) {
    return ConversationType.EMAIL;
  }

  const isAssignedToAI = isUserAssignedConversation(
    AI_AUTOPILOT_USER_ID,
    assignee,
  );
  const isAssignedToChatBot = isUserAssignedConversation(
    CHATBOT_USER_ID,
    assignee,
  );
  if (!assignee || (assignee && !isAssignedToAI && !isAssignedToChatBot)) {
    return ConversationType.LIVE;
  }

  if (isAssignedToAI) {
    return ConversationType.AI;
  } else {
    return ConversationType.CHAT_FLOW;
  }
};

export function createTryItOnTempConversation(
  teamId: string,
  productData: { id: string; title: string; imageUrl: string },
): Conversation {
  const tryItOnPrompt = `Upload your photo to see how ${productData.title} would look on you!`;

  const messages: ConversationMessage[] = [
    // Upload UI message (from AI, triggers the upload interface)
    {
      _id: `${TEMP_CONVERSATION_ID}-upload-ui`,
      content: tryItOnPrompt,
      contentType: ContentType.PRODUCT_VISUALIZATION_UPLOAD_UI,
      type: ConversationMessageType.MERCHANT,
      confirmationStateInformation: {},
      visibility: MessageVisibility.PUBLIC,
      sentAt: new Date(),
      pendingToolCallId: `${TEMP_CONVERSATION_ID}-tool-call`,
      structuredContent: {
        productId: productData.id,
        productImageUrl: productData.imageUrl,
      },
    },
  ];

  return {
    _id: TEMP_CONVERSATION_ID,
    assignee: AI_AUTOPILOT_USER_ID,
    team: teamId,
    messages,
    createdAt: new Date(),
    updatedAt: new Date(),
    platform: ConversationPlatform.REDO_CHAT,
    status: ConversationStatus.OPEN,
    lastResponseAt: new Date(),
    lastKnownStepId: null,
  };
}
